import React from "react";
import moment from "moment";
import { Panel } from "../../../components/panels";
import { formatShortDate } from "../../../utilities";
import { DeleteDialog } from "../../../components";
import { NoDataAvailableDiv } from "../../../components/NoDataAvailableDiv";
import { useCustomQuery } from "../../../utilities/hookutils";
import classNames from "classnames";
import { routes } from "utilities/routing/generatedRoutes";
import { getHasOutstandingBalance } from "utilities/invoiceUtils";
import { isDiscarded, hasDocumentBeenSubmitted, isDocumentOverdue } from "utilities/documentUtils";
import Table from "components/Tables/Table";
import { type TableColumn } from "components/Tables";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { ButtonSecondary } from "components/Atoms/Button";
import InvoiceLifeCyclePill from "invoice/InvoiceLifeCyclePill";
import { useInvoiceV2OrLegacy } from "components/ModernOrLegacy/hooks";
import { apiResponse } from "utilities/apiUtils";
import CreateInvoice from "components/ModernOrLegacy/AppCreateInvoice";

interface Props {
  config: server.dto.LinkedInvoicePanelConfig
  show: server.dto.Show
}

const LinkedInvoicePanel = (props: Props) => {
  const [isUnlinkingDocument, setIsUnlinkingDocument] = React.useState<server.dto.InvoiceForList>(undefined);

  const canModify = site.permissions.invoice.canBatchModify;

  const invoicesForShowQueryKey = "invoicesForShow";
  const invoicesQuery = useCustomQuery({
    queryKey: [invoicesForShowQueryKey, props.show.showId],
    queryFn: async () => window.CoreApi.Invoice.ListInvoiceByShow(props.show.showId).then(r => r.data),
    initialData: apiResponse.success([])
  });

  const unlink = async () => {
    await window.Api.Invoices.UnlinkShow(isUnlinkingDocument.invoiceId, props.show.showId);
    invoicesQuery.refetch();
    setIsUnlinkingDocument(undefined);
  };

  const onNewClick = async () => {
    invoiceV2OrLegacy.displayModernOrLegacy();
  };

  const { refToScrollTo } = useShortcutPanel({
    type: "invoice",
    label: props.config.headerLabel,
    totalItems: invoicesQuery.data.data.length,
    onAddClicked: canModify ? onNewClick : undefined
  });

  const anyESign = invoicesQuery.data.data.some(x => x.isV2);

  const invoiceV2OrLegacy = useInvoiceV2OrLegacy();

  return <Panel
    panelClassName="s1_related-feature-panel"
    panelRef={refToScrollTo}
    title={props.config.headerLabel}
    hasError={invoicesQuery.isError || invoicesQuery.data.hasErrors}
    button={canModify && <ButtonSecondary onClick={onNewClick} label={RESX.Buttons.btnNew} />}
  >
    {invoicesQuery.data.data.length === 0 && <NoDataAvailableDiv />}

    {invoicesQuery.data.data.length > 0 && <Table
      items={invoicesQuery.data.data}
      isLoading={false}
      onEdit={canModify ? (invoice) => window.location.href = (invoice.isV1 ? routes.invoice_Edit(invoice.invoiceId).absolute : routes.invoiceV2_Detail(invoice.invoiceId).absolute) : undefined}
      onDelete={canModify ? (invoice) => { setIsUnlinkingDocument(invoice); } : undefined}
      deleteIcon="linkBreak"
      getKey={invoice => invoice.invoiceId.toString()}
      columns={getLinkedInvoiceColumns(anyESign)}
      hideHeader
    />}

    <CreateInvoice
      modernOrLegacyHook={invoiceV2OrLegacy}
      fromShow={{
        currency: props.show.currency,
        exchangeRate: props.show.exchangeRate,
        addressee: props.show.promoter,
        costCenter: props.show.costCenter,
        projectCode: props.show.projectCode,
        showId: props.show.showId,
        promoterContactId: props.show.promoterContact?.contactId
      }}
    />

    <DeleteDialog
      isOpen={isUnlinkingDocument != null}
      title={RESX.GeneralWarnings.Warning}
      message={RESX.Document.UnlinkWarning}
      onDelete={unlink}
      buttonProps={{
        primary: true,
        color: undefined,
        content: RESX.Buttons.BtnOk
      }}
      handleClose={() => { setIsUnlinkingDocument(undefined); }}
    />

  </Panel>;
};

const getDiscardedClass = (invoice: server.dto.InvoiceForList, otherClasses?: string) => classNames(otherClasses, { s1_gray: invoice != null && isDiscarded(invoice.lifeCycle.value) });

export const getLinkedInvoiceColumns = (anyESign: boolean): Array<TableColumn<server.dto.InvoiceForList>> => [{
  key: "esignIcon",
  header: "",
  size: "36px",
  hidden: !anyESign,
  render: invoice => invoice.isV2 && <i className="icon-rocket" />,
  className: getDiscardedClass
}, {
  key: "date",
  header: "",
  className: getDiscardedClass,
  size: "2fr",
  render: invoice => <div className="flex-column gap-small">
    <a href={invoice.isV1 ? routes.invoice_Detail(invoice.invoiceId).absolute : routes.invoiceV2_Detail(invoice.invoiceId).absolute}
      className={getDiscardedClass(invoice)}>
      {invoice.documentNumber}
    </a>
    <div>
      <InvoiceLifeCyclePill value={invoice.lifeCycle.value} overDue={invoice.overDue} />
    </div>
  </div>
}, {
  key: "addressee",
  header: "",
  className: getDiscardedClass,
  render: invoice => invoice.addressee.name,
  size: "3fr"
},
{
  key: "dates",
  header: "",
  size: "2fr",
  className: getDiscardedClass,
  render: invoice => hasDocumentBeenSubmitted(invoice.lifeCycle.value) && <>
    <div>
      {RESX.DateTime.Send}: {formatShortDate(moment.utc(invoice.sendDate))}
    </div>
    <div className={classNames({ s1_red: isDocumentOverdue(invoice) })}>
      {RESX.DateTime.Due}: {formatShortDate(moment.utc(invoice.dueDate))}
    </div>
  </>
}, {
  key: "total",
  header: "",
  className: invoice => getDiscardedClass(invoice, "s1_cell_financialamount"),
  render: invoice => <>
    {invoice.total != null && invoice.total.amount !== 0 && invoice.total.formatted}
    {getHasOutstandingBalance(invoice) && <div className="s1_red">{invoice.totalOutstanding.formatted}</div>}
  </>
}
];

export default LinkedInvoicePanel;